import React from "react";
import {
  BarChart2,
  Activity,
  AlertTriangle,
  Bell,
  ChevronRight,
  Grid,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";

const Overview = () => {
  const navigate = useNavigate();

  // Sample data - in real app would come from API/context
  const platforms = [
    {
      name: "Platform A",
      modules: {
        total: 4,
        active: 1,
        warning: 1,
        error: 2,
      },
    },
    {
      name: "Platform B",
      modules: {
        total: 4,
        active: 3,
        warning: 1,
        error: 0,
      },
    },
  ];

  const recentAlerts = [
    {
      id: 1,
      module: "SACP Module 3",
      platform: "Platform A",
      message: "Critical: Potential below threshold",
      severity: "error",
      timestamp: "2 minutes ago",
    },
    {
      id: 2,
      module: "SACP Module 7",
      platform: "Platform B",
      message: "Warning: Potential approaching lower limit",
      severity: "warning",
      timestamp: "15 minutes ago",
    },
  ];

  const systemStatus = [
    { name: "Database Connection", status: "connected" },
    { name: "Data Collection Service", status: "running" },
    { name: "Alert System", status: "active" },
  ];

  // Navigation handlers
  const goToModules = (platform = null, status = null) => {
    let path = "/monitoring/module";
    const params = new URLSearchParams();

    if (platform) {
      params.append("platform", platform);
    }

    if (status) {
      params.append("status", status);
    }

    const queryString = params.toString();
    if (queryString) {
      path += `?${queryString}`;
    }

    navigate(path);
  };

  const goToAlerts = () => {
    navigate("/monitoring/alerts");
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">System Overview</h1>
        <Link
          to="/monitoring/alerts"
          className="text-blue-500 hover:text-blue-700 flex items-center gap-2"
        >
          <Bell size={16} />
          View All Alerts
        </Link>
      </div>

      {/* Overall Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Total Modules Card */}
        <div
          className="bg-white p-6 rounded-lg shadow group hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => goToModules()}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-semibold">Total Modules</p>
              <p className="text-3xl font-bold text-blue-600">
                {platforms.reduce((acc, p) => acc + p.modules.total, 0)}
              </p>
            </div>
            <div className="flex items-center text-blue-600">
              <Grid size={24} />
              <ChevronRight
                size={16}
                className="opacity-0 group-hover:opacity-100 transition-opacity"
              />
            </div>
          </div>
        </div>

        {/* Active Modules Card */}
        <div
          className="bg-white p-6 rounded-lg shadow group hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => goToModules(null, "active")}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-semibold">Active Modules</p>
              <p className="text-3xl font-bold text-green-600">
                {platforms.reduce((acc, p) => acc + p.modules.active, 0)}
              </p>
            </div>
            <div className="flex items-center text-green-600">
              <Activity size={24} />
              <ChevronRight
                size={16}
                className="opacity-0 group-hover:opacity-100 transition-opacity"
              />
            </div>
          </div>
        </div>

        {/* Warning Modules Card */}
        <div
          className="bg-white p-6 rounded-lg shadow group hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => goToModules(null, "warning")}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-semibold">Warning Modules</p>
              <p className="text-3xl font-bold text-yellow-600">
                {platforms.reduce((acc, p) => acc + p.modules.warning, 0)}
              </p>
            </div>
            <div className="flex items-center text-yellow-600">
              <AlertTriangle size={24} />
              <ChevronRight
                size={16}
                className="opacity-0 group-hover:opacity-100 transition-opacity"
              />
            </div>
          </div>
        </div>

        {/* Error Modules Card */}
        <div
          className="bg-white p-6 rounded-lg shadow group hover:shadow-md transition-shadow cursor-pointer"
          onClick={() => goToModules(null, "error")}
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="text-lg font-semibold">Error Modules</p>
              <p className="text-3xl font-bold text-red-600">
                {platforms.reduce((acc, p) => acc + p.modules.error, 0)}
              </p>
            </div>
            <div className="flex items-center text-red-600">
              <AlertTriangle size={24} />
              <ChevronRight
                size={16}
                className="opacity-0 group-hover:opacity-100 transition-opacity"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Platform Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {platforms.map((platform) => (
          <div
            key={platform.name}
            className="bg-white rounded-lg shadow-md group hover:shadow-lg transition-shadow cursor-pointer"
            onClick={() => goToModules(platform.name)}
          >
            <div className="p-4 border-b flex justify-between items-center">
              <h2 className="text-xl font-semibold">
                {platform.name} Overview
              </h2>
              <ChevronRight
                size={16}
                className="text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity"
              />
            </div>
            <div className="p-6">
              <div className="flex gap-4">
                <div
                  className="flex-1 text-center cursor-pointer hover:bg-green-50 rounded-lg p-2 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    goToModules(platform.name, "active");
                  }}
                >
                  <div className="text-3xl font-bold text-green-500">
                    {platform.modules.active}
                  </div>
                  <div className="text-sm text-gray-500">Active</div>
                </div>
                <div
                  className="flex-1 text-center cursor-pointer hover:bg-yellow-50 rounded-lg p-2 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    goToModules(platform.name, "warning");
                  }}
                >
                  <div className="text-3xl font-bold text-yellow-500">
                    {platform.modules.warning}
                  </div>
                  <div className="text-sm text-gray-500">Warning</div>
                </div>
                <div
                  className="flex-1 text-center cursor-pointer hover:bg-red-50 rounded-lg p-2 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    goToModules(platform.name, "error");
                  }}
                >
                  <div className="text-3xl font-bold text-red-500">
                    {platform.modules.error}
                  </div>
                  <div className="text-sm text-gray-500">Error</div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Alerts */}
      <div
        className="bg-white rounded-lg shadow-md group hover:shadow-lg transition-shadow cursor-pointer"
        onClick={() => goToAlerts()}
      >
        <div className="p-4 border-b flex justify-between items-center">
          <h2 className="text-xl font-semibold">Recent Alerts</h2>
          <div className="flex items-center gap-2 text-blue-500">
            <span className="text-sm">View all</span>
            <ChevronRight
              size={16}
              className="opacity-0 group-hover:opacity-100 transition-opacity"
            />
          </div>
        </div>
        <div className="p-4">
          <div className="space-y-4">
            {recentAlerts.map((alert) => (
              <div
                key={alert.id}
                className={`p-4 rounded-lg ${
                  alert.severity === "error" ? "bg-red-50" : "bg-yellow-50"
                }`}
              >
                <div className="flex items-center gap-2">
                  <span
                    className={`${
                      alert.severity === "error"
                        ? "text-red-500"
                        : "text-yellow-500"
                    }`}
                  >
                    {alert.severity === "error" ? "🔴" : "⚠️"}
                  </span>
                  <div>
                    <h3 className="font-semibold">{alert.module}</h3>
                    <p className="text-sm text-gray-600">{alert.message}</p>
                    <p className="text-xs text-gray-400 mt-1">
                      {alert.timestamp}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* System Status */}
      <div className="bg-white rounded-lg shadow-md">
        <div className="p-4 border-b">
          <h2 className="text-xl font-semibold">System Status</h2>
        </div>
        <div className="p-4">
          <div className="space-y-4">
            {systemStatus.map((service, index) => (
              <div
                key={index}
                className="flex justify-between items-center p-3 bg-gray-50 rounded"
              >
                <span>{service.name}</span>
                <span className="px-3 py-1 bg-green-100 text-green-800 rounded">
                  {service.status}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Overview;
